// A browser-side stand-in for the Choir Durable Object. Used when the network is
// unreachable (or when the page is served as plain static files) so the planet
// still sings, with ghosts for company.

import { COLOS, type Colo } from "../shared/colos";
import { desiredGhosts, ghostMove, summonGhost, type GhostState } from "../shared/ghosts";
import { BAR_MS, DEFAULT_TTL_BARS, barIndex, voiceForContinent } from "../shared/music";
import { hash32, hueFor, nameFor, rng } from "../shared/names";
import { cellAlive, cellKey, type Cell, type ClientMsg, type PlayerInfo, type ServerMsg } from "../shared/protocol";

const REGION_TO_CONTINENT: Record<string, string> = { EU: "EU", NA: "NA", SA: "SA", AF: "AF", AP: "AS", ME: "AS", OC: "OC" };

/** Best guess at where the browser is, from its time zone name ("Europe/Warsaw" → WAW). */
export function guessColo(): Colo {
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    tz = "";
  }
  const city = tz.split("/").pop()?.replace(/_/g, " ").toLowerCase() ?? "";
  if (city) {
    const exact = COLOS.find((c) => c.city.toLowerCase() === city);
    if (exact) return exact;
    const partial = COLOS.find((c) => c.city.toLowerCase().includes(city) || city.includes(c.city.toLowerCase()));
    if (partial) return partial;
  }
  // Fall back to longitude from the UTC offset and a temperate latitude.
  const lon = -new Date().getTimezoneOffset() / 4;
  let best = COLOS[0]!;
  let bestD = Infinity;
  for (const c of COLOS) {
    const d = Math.abs(c.lon - lon) + Math.abs(c.lat - 42) * 0.5;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

export class LocalChoir {
  private cells = new Map<number, Cell>();
  private ghosts: GhostState[] = [];
  private timer: number | undefined;
  private me: PlayerInfo;

  constructor(private readonly room: string, private readonly deliver: (m: ServerMsg) => void) {
    const colo = guessColo();
    const id = `local-${Math.random().toString(36).slice(2)}`;
    this.me = {
      id,
      name: nameFor(id),
      hue: hueFor(id),
      voice: voiceForContinent(REGION_TO_CONTINENT[colo.region]),
      lat: colo.lat,
      lon: colo.lon,
      city: colo.city,
      country: colo.country,
      continent: REGION_TO_CONTINENT[colo.region] ?? "",
      colo: colo.iata,
      joinedAt: Date.now(),
    };
  }

  start(): void {
    const bar = barIndex(Date.now(), 0);
    const want = desiredGhosts(1);
    for (let i = 0; i < want; i++) {
      const taken = new Set([this.me.colo, ...this.ghosts.map((g) => g.player.colo)]);
      this.ghosts.push(summonGhost(bar, `${this.room}:local:${i}`, taken));
    }
    const home = COLOS.find((c) => c.iata === this.me.colo) ?? COLOS[0]!;
    // Seed a few notes so the first bar is not empty.
    const random = rng(hash32(`${this.room}:${bar}`));
    for (let i = 0; i < 6; i++) {
      for (const g of this.ghosts) {
        const { place } = ghostMove(g, this.cells, bar - Math.floor(random() * 10), random);
        if (place) this.cells.set(cellKey(place.step, place.lane), place);
      }
    }
    this.deliver({
      t: "welcome",
      room: this.room,
      you: this.me,
      players: [this.me, ...this.ghosts.map((g) => g.player)],
      cells: [...this.cells.values()],
      epoch: 0,
      now: Date.now(),
      home: { colo: home.iata, city: home.city, lat: home.lat, lon: home.lon },
      stats: { online: 1, notesEver: this.cells.size, visitorsEver: 1, countries: 1, colos: 1, byColo: { [this.me.colo]: 1 } },
    });
    this.timer = window.setInterval(() => this.tick(), BAR_MS * 2);
  }

  stop(): void {
    window.clearInterval(this.timer);
  }

  private tick(): void {
    const now = Date.now();
    const bar = barIndex(now, 0);
    for (const [k, c] of this.cells) if (!cellAlive(c, bar)) this.cells.delete(k);
    // Occasionally a ghost drifts away and another arrives elsewhere.
    this.ghosts = this.ghosts.filter((g) => {
      if (g.leaveBar > bar) return true;
      this.deliver({ t: "leave", id: g.player.id });
      return false;
    });
    while (this.ghosts.length < desiredGhosts(1)) {
      const taken = new Set([this.me.colo, ...this.ghosts.map((g) => g.player.colo)]);
      const g = summonGhost(bar, `${this.room}:local:${bar}:${this.ghosts.length}`, taken);
      this.ghosts.push(g);
      this.deliver({ t: "join", player: g.player });
    }
    const random = rng(hash32(`${this.room}:${bar}:${this.cells.size}`));
    for (const g of this.ghosts) {
      const { place, remove } = ghostMove(g, this.cells, bar, random);
      if (remove !== null) {
        const c = this.cells.get(remove);
        if (c) {
          this.cells.delete(remove);
          this.deliver({ t: "cell", step: c.step, lane: c.lane, cell: null, by: g.player.id, at: now });
        }
      }
      if (place) {
        this.cells.set(cellKey(place.step, place.lane), place);
        this.deliver({ t: "cell", step: place.step, lane: place.lane, cell: place, by: g.player.id, at: now });
      }
    }
  }

  receive(msg: ClientMsg): void {
    const now = Date.now();
    switch (msg.t) {
      case "ping":
        window.setTimeout(() => this.deliver({ t: "pong", c: msg.c, s: msg.c }), 0);
        return;
      case "toggle": {
        const k = cellKey(msg.step, msg.lane);
        const bar = barIndex(now, 0);
        const existing = this.cells.get(k);
        if (existing && cellAlive(existing, bar)) {
          this.cells.delete(k);
          this.deliver({ t: "cell", step: msg.step, lane: msg.lane, cell: null, by: this.me.id, at: now });
        } else {
          const p = this.me;
          const cell: Cell = {
            step: msg.step,
            lane: msg.lane,
            owner: p.id,
            ownerName: p.name,
            hue: p.hue,
            voice: p.voice,
            lat: p.lat,
            lon: p.lon,
            colo: p.colo,
            born: bar,
            ttl: DEFAULT_TTL_BARS,
          };
          this.cells.set(k, cell);
          this.deliver({ t: "cell", step: msg.step, lane: msg.lane, cell, by: p.id, at: now });
        }
        return;
      }
      case "rename":
        this.me = { ...this.me, name: msg.name };
        this.deliver({ t: "join", player: this.me });
        return;
      default:
        return;
    }
  }
}
